import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  RelateBidToLeadMutation,
  RelateBidToLeadMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "../../../tracking";
import { updateBidQuery } from "../bid/bids/update_cache/updateBidEntityCache";
import { useGraphQlMutation } from "../useGraphQlClient";
import { updateLeadQuery } from "./update_cache/updateLeadEntityCache";

const RELATE_BID_TO_LEAD_MUTATION = graphql(`
  mutation RelateBidToLead($req: RelateBidToLeadRequest!) {
    relateBidToLead(RelateBidToLeadRequest: $req) {
      leadId
      leadName
      bid {
        id
        name
      }
    }
  }
`);

export function useRelateBidToLead(
  options: UseMutationOptions<
    RelateBidToLeadMutation,
    unknown,
    RelateBidToLeadMutationVariables,
    unknown
  >,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(RELATE_BID_TO_LEAD_MUTATION, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const { leadId, bid, leadName } = data.relateBidToLead;

      logEvent(EventNames.leadDetailsUpdated, {
        "Lead ID": leadId,
        "Field updated": "Bid ID",
        "Updated field content": bid?.id ?? null,
      });

      updateLeadQuery(queryClient, leadId, (prevData) => {
        return {
          ...prevData,
          ...(bid ? { bid: { id: bid.id, name: bid.name } } : { bid: null }),
        };
      });

      const bidId = bid?.id ?? variables.req.bidId;

      // BidId will be set, because it's either coming from the response (assigning a bid to a lead)
      //  or from the request (removing a bid from a lead)
      if (bidId) {
        updateBidQuery(queryClient, bidId, (prevData) => {
          return {
            ...prevData,
            ...(bid ? { lead: { id: leadId, name: leadName } } : { lead: null }),
          };
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
  return { data, ...rest };
}
